import React, { useState, useMemo, useRef } from 'react';
import { View, TouchableOpacity, ScrollView } from 'react-native';
import { Table, Typography, useTheme, Dropdown, Button, Checkbox } from '@hmsexp/component-library';
import { feedBackGridStyles } from '../styles.js';
import TableCellFeedBack from './TableCells.js';
import get from 'lodash/get';
import TooltipWrapper from './TooltipWrapper';
import CheckBoxCell from './CheckBoxCell.js';
import AlertBadge from './AlertsBadge.js';
import { CHECKBOX, TABLE_HEADERS, CLOSE_ALERT } from '../constants/index.js';
import { useWindowDimensions } from 'react-native';
import EmojiMap from '../../../components/Common/EmojiMap/index.js';
import OtaIcon from '../../../components/Common/EmojiMap/OtaIcon.js';

const FeedBackGrid = ({
  feedbackdata,
  feedbackwidget,
  sortingPayload,
  tableTitle,
  currentPage,
  role,
  handleOnSortClick
}) => {

  const theme = useTheme();
  const { palette } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const styles = feedBackGridStyles(theme, isMobile);
  const scrollViewRef = useRef(null);

  let feedbackTableHeaders = get(feedbackwidget, TABLE_HEADERS, []) || [];

  if (role === 'muo') {
    feedbackTableHeaders = get(feedbackwidget, TABLE_HEADERS, []) || [];
  } else {
    feedbackTableHeaders = get(feedbackwidget, 'table_headers_property', []) || [];
  }
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [selectedFeedbackIds, setSelectedFeedbackIds] = useState(new Set());
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [showOriginalComments, setShowOriginalComments] = useState(false);

  const allFeedbackIds = useMemo(() => {
    return Array.isArray(feedbackdata?.feedbacks)
      ? feedbackdata.feedbacks.map(feedback => feedback.feedback_id).filter(Boolean)
      : [];
  }, [feedbackdata, currentPage]);

  const isAllSelected = allFeedbackIds.length > 0 && allFeedbackIds.every(feedback_id => selectedFeedbackIds.has(feedback_id));
  const statusDisabled = feedbackwidget?.status_dropdown?.disabled;
  const tagsDisabled = feedbackwidget?.tags_dropdown?.disabled;
  const originallanguageCheckboxDisabled = feedbackwidget?.original_language_checkbox?.disabled;

  const handleSelectAll = isSelected => {
    if (isSelected) {
      setSelectedFeedbackIds(new Set(allFeedbackIds));
    } else {
      setSelectedFeedbackIds(new Set());
    }
  };

  const handleRowSelect = (row, isSelected) => {
    const feedbackId = row?.feedback_id;
    const newSelectedIds = new Set(selectedFeedbackIds);

    if (isSelected) {
      newSelectedIds.add(feedbackId);
    } else {
      newSelectedIds.delete(feedbackId);
    }

    setSelectedFeedbackIds(newSelectedIds);
  };

  const isCloseAlertEnabled = useMemo(() => {
    if (selectedFeedbackIds.size === 0) return false;

    const validStatuses = new Set(['NEW', 'IN_PROGRESS', 'OVERDUE']);

    return feedbackdata.feedbacks.some(fb =>
      selectedFeedbackIds.has(fb.feedback_id) &&
      validStatuses.has(fb.alert_status)
    );
  }, [selectedFeedbackIds, feedbackdata?.feedbacks]);

  const feedbackColumns = Array.isArray(feedbackTableHeaders)
    ? feedbackTableHeaders.map((column, index) => {
      const mappedColumn = {
        ...column,
        field: column.key,
        width: column.width,
        flexDirection: 'row',
        alignItems: 'flex-start',
        onHover: column.onHover || false,
        Cell: props => (
          <TableCellFeedBack
            {...props}
            index={index}
            sortingPayload={sortingPayload}
            handleOnSortClick={handleOnSortClick}
          />
        )
      };

      if (column.key === CHECKBOX) {
        mappedColumn.isAllSelected = isAllSelected;
        mappedColumn.handleAllSelectCb = handleSelectAll;
      }

      return mappedColumn;
    })
    : [];

  const feedbackTableData = Array.isArray(feedbackdata?.feedbacks)
    ? feedbackdata.feedbacks.map(row => {
      const formattedRow = {};

      feedbackColumns.forEach(col => {
        if (col?.field !== CHECKBOX) {
          const fieldKey = col.field;
          let value;

          if (fieldKey === 'comments') {
            value = showOriginalComments
              ? get(row, 'original_comments', '')?.trim()
                ? get(row, 'original_comments', '-')
                : get(row, 'comments', '-')
              : get(row, 'comments', '-');
          } else if (fieldKey === 'checkout_date') {
            const arr = get(row, fieldKey, null);
            if (Array.isArray(arr)) {
              const [yyyy, mm, dd] = arr;
              value = `${dd.toString().padStart(2, '0')}/${mm.toString().padStart(2, '0')}/${yyyy}`;
            } else {
              value = '-';
            }
          } else if (fieldKey === 'response_date') {
            const arr = get(row, fieldKey, null);
            if (Array.isArray(arr) && arr.length >= 6) {
              let [yyyy, mm, dd, hh, min] = arr;

              const ampm = hh >= 12 ? 'PM' : 'AM';
              let hour12 = hh % 12;
              hour12 = hour12 === 0 ? 12 : hour12;

              value = `${mm.toString().padStart(2, '0')}/${dd.toString().padStart(2, '0')}/${yyyy}, ${hour12
                .toString()
                .padStart(2, '0')}:${min.toString().padStart(2, '0')} ${ampm}`;
            } else {
              value = '-';
            }
          } else if (fieldKey === 'property_name') {
            const propertyName = get(row, 'property_name', '-');
            const propertyId = get(row, 'property_id', '-');

            value = `${propertyId} ${propertyName}`;
          } else if (fieldKey === 'activity_count') {
            const activityCount = get(row, fieldKey, '0');
            if (activityCount === 0 || activityCount === '0' || activityCount === null) {
              value = '-';
            } else {
              value = activityCount;
            }
          } else if (fieldKey === 'alert') {
            formattedRow['alert'] = {
              status: get(row, 'alert_status', '-'),
              type: get(row, 'alert_type', '-')
            };
          } else {
            value = get(row, fieldKey, '-');
          }

          formattedRow.isSelected = selectedFeedbackIds.has(row?.feedback_id);
          formattedRow.onSelectClick = handleRowSelect;
          formattedRow[col.field] = value === undefined || value === null || value === '' ? '-' : value;
        }
      });

      return formattedRow;
    })
    : [];

  const getEmojiType = score => {
    const num = Number(score);
    if (num >= 4.0) return 'HAPPY';
    if (num >= 3) return 'NEUTRAL';
    return 'SAD';
  };

  const renderRow = ({ row, columns }) => {
    const isHovered = hoveredRowId === row?.feedback_id;
    const rowStyle = {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: theme.palette.primaryOutline,
      backgroundColor: isHovered
        ? palette.hyperlinkBackgroundLow
        : row.isSelected
          ? theme.palette.primaryBackgroundLow
          : 'transparent',
      cursor: 'pointer'
    };

    return (
      <TouchableOpacity onPress={() => onRowClick(row)}>
        <View
          style={rowStyle}
          onMouseEnter={() => setHoveredRowId(row?.feedback_id)}
          onMouseLeave={() => setHoveredRowId(null)}>
          {columns.map((column, index) => {
            const cellStyle = {
              flexDirection: 'row',
              width: column?.width,
              minWidth: column?.width,
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexShrink: 0,
              flexWrap: 'wrap',
              position: 'relative',
              paddingLeft: 20,
              marginRight: 20,
              flexDirection: 'row',
            };

            const CellComponent = ({ row }) => (
              <Typography
                variant='subtitle2'
                color='primaryContentHigh'
                fontWeightType='regular'
                textProps={{
                  numberOfLines: 2,
                  ellipsizeMode: 'tail',
                  style: {
                    width: column?.width,
                    minWidth: column?.width,
                    paddingHorizontal: 3,
                    marginRight: 8,
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    position: 'relative'
                  }
                }}>
                {row[column.field]}
              </Typography>
            );
            if (column?.disable === true) {
              return null;
            }

            if (column.field === 'alert') {
              const status = row?.alert_status;
              const type = row?.alert_type;

              const isEmpty = !status || status === '-' || !type || type === '-';

              if (isEmpty) {
                return (
                  <View key={index} style={cellStyle}>
                    <Typography
                      variant='subtitle2'
                      color='primaryContentHigh'
                      fontWeightType='regular'
                      textProps={{
                        numberOfLines: 2,
                        ellipsizeMode: 'tail',
                        style: {
                          width: column?.width,
                          minWidth: column?.width,
                          paddingHorizontal: 3,
                          marginRight: 20,
                          flexShrink: 0,
                          flexWrap: 'wrap',
                          position: 'relative'
                        }
                      }}>
                      -
                    </Typography>
                  </View>
                );
              }

              return (
                <View key={index} style={cellStyle}>
                  <AlertBadge alertStatus={status} alertType={type} />
                </View>
              );
            }
            if (column?.field === CHECKBOX) {
              return (
                <View
                  key={index}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: column?.width,
                    minWidth: column?.width,
                    paddingHorizontal: 6,
                    marginRight: 8
                  }}>
                  <CheckBoxCell row={row} isHeaderCell={false} />
                </View>
              );
            }

            if (column?.onhover || false) {
              return (
                <View key={index} style={cellStyle}>
                  <TooltipWrapper overlay={row[column.field]}>
                    <CellComponent row={row} />
                  </TooltipWrapper>
                </View>
              );
            }

            if (column?.field === 'overall_score') {
              if (row?.feedback_type === 'Survey') {
                return (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      width: column?.width,
                      minWidth: column?.width,
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      marginRight: 8,
                      flexShrink: 0,
                      position: 'relative',
                      paddingHorizontal: 20
                    }}>
                    <EmojiMap type={getEmojiType(row[column?.field])} />

                    <CellComponent row={row} />
                  </View>
                );
              } else if (row?.feedback_type === 'Social media' && row.feedback_source) {
                return (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      width: column?.width,
                      minWidth: column?.width,
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      marginRight: 8,
                      flexShrink: 0,
                      position: 'relative',
                      paddingHorizontal: 20
                    }}>
                    <OtaIcon source={row?.feedback_source} />

                    <CellComponent row={row} />
                  </View>
                );
              } else {
                return (
                  <View
                    key={index}
                    style={{
                      flexDirection: 'row',
                      width: column?.width,
                      minWidth: column?.width,
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      marginRight: 8,
                      flexShrink: 0,
                      position: 'relative',
                      paddingHorizontal: 20
                    }}>
                    <EmojiMap type={getEmojiType(row[column.field])} />

                    <CellComponent row={row} />
                  </View>
                );
              }
            }

            return (
              <View key={index} style={cellStyle}>
                <CellComponent row={row} />
              </View>
            );
          })}
        </View>
      </TouchableOpacity>
    );
  };

  const onRowClick = row => {
    const feedback_id = row?.feedback_id;
    window.open(`/dashboard/feedback_details/${feedback_id}`, '_blank');
  };

  const showtableTitle = () => {
    return (
      <View style={styles.titleWrapper}>
        <Typography color='primaryContentHigh' fontWeightType='regular' variant='body2'>
          {tableTitle}
        </Typography>
        {role === 'muo' && (
          <View style={styles.filtersContainer}>
            {!statusDisabled && (
              <View style={styles.filterItem}>
                <Typography color='primaryContentMedium' fontWeightType='regular' variant='subtitle2'>
                  Statuses
                </Typography>
                <Dropdown
                  items={[
                    { label: 'All', value: 'all' },
                    { label: 'Unread', value: 'unread' },
                    { label: 'Read', value: 'read' },
                    { label: 'Subscribed', value: 'subscribed' },
                    { label: 'Unacknowledged', value: 'unacknoledged' }
                  ]}
                  value={selectedStatus}
                  onChange={value => setSelectedStatus(value)}
                />
              </View>
            )}
            {!tagsDisabled && (
              <View style={styles.filterItem}>
                <Typography color='primaryContentMedium' fontWeightType='regular' variant='subtitle2'>
                  Tags
                </Typography>
                <Dropdown
                  items={[
                    { label: 'All', value: 'all' },
                    { label: 'No Tag', value: 'no_tag' },
                    { label: 'Yellow', value: 'yellow' },
                    { label: 'Red', value: 'red' },
                    { label: 'Green', value: 'green' },
                    { label: 'Important', value: 'important' }
                  ]}
                  value={selectedTag}
                  onChange={value => setSelectedTag(value)}
                />
              </View>
            )}
          </View>
        )}
      </View>
    );
  };

  const OriginalLanguageCheckbox = () => {
    return (
      <Checkbox
        options={[{ label: 'Show comments in original language', value: 'original' }]}
        values={showOriginalComments ? ['original'] : []}
        onChange={newValues => setShowOriginalComments(newValues.includes('original'))}
        secondary={false}
        rightAligned={false}
        labelKey='label'
        valueKey='value'
        labelSize='subtitle2'
        style={{
          checkboxRowsWrapper: { marginBottom: 12 },
          optionContainer: { marginRight: 12 }
        }}
      />
    );
  };

  const BulkActionCTA = () => {

    const responseText =
      selectedFeedbackIds.size === 1 ? '1 Response Selected' : `${selectedFeedbackIds.size} Responses Selected`;

    return (
      <View onMouseEnter={() => setIsButtonHovered(true)} onMouseLeave={() => setIsButtonHovered(false)}>
        <View style={{ width: 150, marginTop: 20 }}>
          <Button
            disabled={!isCloseAlertEnabled}
            outlined
            title={CLOSE_ALERT}
            style={{
              buttonContainer: {
                marginBottom: 12,
                width: '100%',
                backgroundColor:
                  isCloseAlertEnabled && isButtonHovered ? palette.hyperlinkBackgroundLow : 'transparent',
                borderColor: isCloseAlertEnabled ? palette.hyperlinkBackgroundHigh : palette.primaryOutline
              },
              buttonText: {
                color: isCloseAlertEnabled ? palette.hyperlinkBackgroundHigh : palette.primaryOutline,
                fontWeight: '600'
              }
            }}
            onClick={() => console.log('Mark as read clicked')}
          />
          {selectedFeedbackIds.size && (
            <Typography
              variant='body2'
              color='primaryContentMedium'
              fontWeightType='regular'
              textProps={{
                style: { textAlign: 'center' }
              }}>
              {responseText}
            </Typography>
          )}
        </View>
      </View>
    );
  };

  if (!feedbackdata || !Array.isArray(feedbackdata.feedbacks) || feedbackdata.feedbacks.length === 0) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}>
        <Typography variant='body1' color='primaryContentMedium'>
          No data available
        </Typography>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, position: 'relative' }}>
      {isMobile ? (
        <View style={{ top: 120, left: 13, zIndex: 1000, paddingHorizontal: 16, paddingVertical: 10 }}>
          {!originallanguageCheckboxDisabled && <OriginalLanguageCheckbox />}
        </View>
      ) : (
        <View
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            zIndex: 1000,
            backgroundColor: theme.palette.primarySurface || '#FFFFFF',
            borderRadius: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            paddingRight: 12
          }}>
          {!originallanguageCheckboxDisabled && <OriginalLanguageCheckbox />}
          {/* {<BulkActionCTA />} */}
        </View>
      )}

      <ScrollView
        ref={scrollViewRef}
        horizontal={false}
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
        scrollEventThrottle={16}>
        <View style={{ flex: 1 }}>
          <Table
            data={feedbackTableData}
            columns={feedbackColumns}
            renderTitle={showtableTitle}
            isVerticalScrollEnabled={true}
            showTitleBar={true}
            renderRow={renderRow}
            style={{
              container: styles.container,
              headerContainer: styles.headerContainer,
              titleContainer: styles.titleWrapper
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

export default FeedBackGrid;